/**
 * @fileoverview Painel do modo "Lista salva" no node bulk_message:
 * lista segmentos da company e, ao selecionar, mostra a contagem efetiva da
 * lista + botão que abre o modal de gestão de contatos (excluir/restaurar).
 * A listagem/edição vive no modal — aqui fica enxuto pra não truncar nomes.
 */

import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { Users, RefreshCw, Trash2, Plus, Settings2 } from "lucide-react";
import useContactSegments from "../../../../hooks/useContactSegments";
import ContactSegmentManagerModal from "../components/ContactSegmentManagerModal";

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.7)",
  marginBottom: 4,
  display: "block",
};

const BulkListPanel = ({
  segmentId,
  onSegmentChange,
  onCreateNew,
  toastError,
}) => {
  const { segments, loading, refresh, remove, listSegmentContacts } =
    useContactSegments();

  const [count, setCount] = useState(0);
  const [excludedCount, setExcludedCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const selectedSegment = segments.find((s) => s.id === segmentId);

  const fetchCount = useCallback(async () => {
    if (!segmentId) {
      setCount(0);
      setExcludedCount(0);
      return;
    }
    setCountLoading(true);
    try {
      // página 1 só pra ler os totais efetivos (com exclusões aplicadas)
      const data = await listSegmentContacts(segmentId, { pageNumber: 1 });
      setCount(data.count || 0);
      setExcludedCount(data.excludedCount || 0);
    } catch (err) {
      setCount(0);
      setExcludedCount(0);
    } finally {
      setCountLoading(false);
    }
  }, [segmentId, listSegmentContacts]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deletar a lista "${name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await remove(id);
      if (segmentId === id) onSegmentChange(null);
    } catch (err) {
      if (toastError) toastError(err);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <label style={{ ...labelStyle, flex: 1, marginBottom: 0 }}>
          Listas salvas da empresa
        </label>
        <div
          onClick={refresh}
          title="Recarregar"
          style={{
            cursor: "pointer",
            padding: 4,
            color: "rgba(255,255,255,0.5)",
            display: "inline-flex",
          }}
        >
          <RefreshCw size={12} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "10px 0", textAlign: "center" }}>
          <CircularProgress size={16} style={{ color: "#818cf8" }} />
        </div>
      ) : segments.length === 0 ? (
        <div
          style={{
            padding: "12px",
            borderRadius: 8,
            border: "1px dashed rgba(255,255,255,0.12)",
            fontSize: 11,
            color: "rgba(255,255,255,0.5)",
            textAlign: "center",
          }}
        >
          Nenhuma lista salva ainda.
          <div
            onClick={onCreateNew}
            style={{
              marginTop: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 12px",
              borderRadius: 6,
              background: "rgba(99,102,241,0.18)",
              color: "#a5b4fc",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <Plus size={12} /> Criar uma lista
          </div>
        </div>
      ) : (
        <div
          style={{
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 8,
          }}
        >
          {segments.map((s) => {
            const active = s.id === segmentId;
            return (
              <div
                key={s.id}
                onClick={() => onSegmentChange(s.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  background: active ? "rgba(99,102,241,0.14)" : "transparent",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  transition: "background 120ms ease",
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    border: active ? "none" : "1px solid rgba(255,255,255,0.2)",
                    background: active ? "#818cf8" : "transparent",
                    flex: "0 0 12px",
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 12,
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: active ? 600 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.name}
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(s.id, s.name);
                  }}
                  title="Deletar"
                  style={{
                    padding: 3,
                    display: "inline-flex",
                    color: "rgba(239,68,68,0.6)",
                    cursor: "pointer",
                  }}
                >
                  <Trash2 size={12} />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Resumo da lista selecionada + atalho pro modal de gestão */}
      {segmentId && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(99,102,241,0.08)",
            border: "1px solid rgba(99,102,241,0.22)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {countLoading ? (
              <CircularProgress size={14} style={{ color: "#818cf8" }} />
            ) : (
              <Users size={14} color="#818cf8" />
            )}
            <span style={{ fontSize: 12, fontWeight: 600, color: "#a5b4fc", flex: 1 }}>
              {countLoading
                ? "Calculando…"
                : `${count.toLocaleString("pt-BR")} contato${count === 1 ? "" : "s"} na lista`}
            </span>
            {excludedCount > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 7px",
                  borderRadius: 999,
                  background: "rgba(239,68,68,0.16)",
                  color: "#fca5a5",
                }}
                title="Contatos removidos manualmente desta lista"
              >
                {excludedCount} excluído{excludedCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <div
            onClick={() => setModalOpen(true)}
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 10px",
              borderRadius: 6,
              background: "rgba(99,102,241,0.16)",
              border: "1px solid rgba(99,102,241,0.3)",
              color: "#c7d2fe",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <Settings2 size={13} /> Gerenciar contatos
          </div>
        </div>
      )}

      {segments.length > 0 && (
        <div
          onClick={onCreateNew}
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "5px 10px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.04)",
            border: "1px dashed rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.6)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <Plus size={11} /> Criar nova lista
        </div>
      )}

      <ContactSegmentManagerModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          fetchCount(); // exclusões podem ter mudado a contagem
        }}
        segmentId={segmentId}
        segmentName={selectedSegment?.name}
        toastError={toastError}
      />
    </div>
  );
};

export default BulkListPanel;
